/* -----------------Globals--------------- */
import { useState } from "react";
import { useTranslation } from "react-i18next";

/* -----------------UI--------------- */
import { EmptyState, Loader, ScrollContainer } from "@memorang/ui";

/* -----------------Helpers & Hooks--------------- */
import { useExamContext } from "@features/exam";
import { getProgressCardsData } from "@helpers/progress-data";
/* -----------------Types--------------- */
import type { FetchMasteryOvertimeResponse } from "../types";

/* -----------------Child components--------------- */
import ProgressView from "../components/Progress/ProgressView";

/* -----------------Assets--------------- */
import { ProgressEmptySvg } from "@memorang/media";

import { SessionConfigDialog } from "@features/session-config";
import { getShowStudyPackViews } from "@helpers/exams";
import { useCustomNavigation } from "@hooks/useCustomNavigation";
import { useDevice } from "@hooks/useDevice";
import type { MixedItemDist } from "@memorang/types";
import { router } from "expo-router";

import { useAppTheme } from "@hooks/useAppTheme";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import useMixedItemDist from "@hooks/useMixedItemDist";
import ProethicaProgressContainer from "./ProethicaProgressContainer";
const ProgressContainer = () => {
	const demoExamId = useDemoExamStore((state) => state.currentExam.id);
	const { data: mixedItemDist, isLoading } = useMixedItemDist();

	if (demoExamId === "proethica") {
		return <ProethicaProgressContainer />;
	}

	if (isLoading || !mixedItemDist) {
		return <Loader />;
	}

	return <PreloadedProgressView mixedItemDist={mixedItemDist} />;
};

export default ProgressContainer;

const PreloadedProgressView = ({
	mixedItemDist,
	masteryOvertime,
}: {
	mixedItemDist: MixedItemDist;
	masteryOvertime?: FetchMasteryOvertimeResponse;
}) => {
	const { t } = useTranslation();

	const [showSessionConfigDialog, setShowSessionConfigDialog] = useState(false);

	const toggleSessionConfigDialog = () => {
		setShowSessionConfigDialog(!showSessionConfigDialog);
	};

	const { handleSessionConfigNavigation } = useCustomNavigation();
	const { isMediumWindowSize } = useDevice();
	const {
		currentExamContext: { studyPackProductId },
	} = useExamContext();
	const theme = useAppTheme();
	const contentIds = mixedItemDist.contents.map((item) => item?.id!);

	const {
		masteryTagsProgressData,
		needsImprovementProgressData,
		progressPageSummaryCardData,
		practiceTestsCardsData,
		showEmptyState,
		itemTypeProgressData,
		numItemsAttempted,
	} = getProgressCardsData(mixedItemDist, "Progress", true, t, theme);

	const showStudyPackViews = getShowStudyPackViews(studyPackProductId);

	return (
		<ScrollContainer
			style={{
				backgroundColor: theme.colors.background,
			}}
			contentContainerStyle={{ padding: 16 }}
			maxWidth={isMediumWindowSize ? "sm" : "md"}
		>
			{showEmptyState ? (
				<EmptyState
					title={t("emptyState.progress.title")}
					subtitle={t("emptyState.progress.subtitle")}
					cta={t("emptyState.progress.cta")}
					image={ProgressEmptySvg}
					handleAction={() => {
						if (showStudyPackViews) {
							handleSessionConfigNavigation(toggleSessionConfigDialog);
						} else {
							router.push("/practice-tests");
						}
					}}
				/>
			) : (
				<ProgressView
					masteryTagsProgressData={masteryTagsProgressData}
					needsImprovementProgressData={needsImprovementProgressData}
					contentIds={contentIds}
					summaryCardData={progressPageSummaryCardData}
					practiceTestsCardData={practiceTestsCardsData}
					showStudyPackViews={showStudyPackViews}
					itemTypeProgressData={itemTypeProgressData}
					numItemsAttempted={numItemsAttempted}
				/>
			)}
			{showSessionConfigDialog ? (
				<SessionConfigDialog
					visible={showSessionConfigDialog}
					handleClose={toggleSessionConfigDialog}
				/>
			) : null}
		</ScrollContainer>
	);
};
